import ExcelJS from 'exceljs';

/**
 * Convert Excel file to CSV format
 * @param {File} excelFile - The uploaded Excel file
 * @returns {Promise<Blob>} - The converted CSV Blob
 */
export const convertExcelToCSV = async (excelFile) => {
  try {
    // Read the Excel file
    const arrayBuffer = await excelFile.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    
    // Get the first worksheet
    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      throw new Error('No worksheet found in the Excel file');
    }
    
    // Convert to CSV
    const csvRows = [];
    
    // Process each row
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      const rowValues = [];
      
      // Process each cell in the row
      row.eachCell({ includeEmpty: true }, (cell) => {
        let value = cell.value;
        
        // Handle special cell types (dates, formulas, etc.)
        if (cell.type === ExcelJS.ValueType.Date) {
          value = cell.value.toLocaleDateString('el-GR'); // Format date as DD/MM/YYYY
        } else if (cell.type === ExcelJS.ValueType.Formula) {
          value = cell.result;
        }
        
        // Escape quotation marks and commas in values
        if (typeof value === 'string') {
          if (value.includes('"') || value.includes(',')) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
        }
        
        rowValues.push(value || '');
      });
      
      csvRows.push(rowValues.join(','));
    });
    
    // Create CSV string and convert to Blob
    const csvContent = csvRows.join('\n');
    return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  } catch (error) {
    console.error('Error converting Excel to CSV:', error);
    throw error;
  }
};

/**
 * Validate insurance data format
 * @param {File} file - The uploaded file
 * @returns {Promise<{isValid: boolean, message: string}>} - Validation result
 */
export const validateInsuranceFile = async (file) => {
  try {
    // Check file type
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    const isCsv = file.name.endsWith('.csv');
    
    if (!isExcel && !isCsv) {
      return { 
        isValid: false, 
        message: 'Please upload an Excel (.xlsx, .xls) or CSV file' 
      };
    }
    
    if (isCsv) {
      const text = await file.text();
      const lines = text.split('\n');
      if (lines.length < 2) {
        return { 
          isValid: false, 
          message: 'CSV file appears to be empty or invalid' 
        };
      }
      return { isValid: true, message: 'CSV file looks valid' };
    }
    
    // For Excel files, we'll check the basic structure
    const arrayBuffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    
    const worksheet = workbook.worksheets[0];
    if (!worksheet || worksheet.rowCount < 2) {
      return { 
        isValid: false, 
        message: 'Excel file appears to be empty or invalid' 
      };
    }
    
    // Check if the first row contains the expected headers
    const firstRow = worksheet.getRow(1);
    const headers = [];
    firstRow.eachCell({ includeEmpty: false }, (cell) => {
      headers.push(String(cell.value).toLowerCase());
    });
    
    // Check for minimum required columns
    const hasLicensePlate = headers.some(h => h.includes('license') || h.includes('plate') || h.includes('χαρακτ'));
    const hasProvider = headers.some(h => h.includes('provider') || h.includes('company') || h.includes('εταιρ'));
    const hasStartDate = headers.some(h => h.includes('date') || h.includes('start') || h.includes('έναρξ'));
    
    if (!hasLicensePlate || !hasProvider || !hasStartDate) {
      return { 
        isValid: false, 
        message: 'Excel file is missing required columns: License Plate, Provider/Company, and Start Date' 
      };
    }
    
    return { isValid: true, message: 'Excel file looks valid' };
  } catch (error) {
    console.error('Error validating insurance file:', error);
    return { 
      isValid: false, 
      message: `Error validating file: ${error.message}` 
    };
  }
};
